import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { 
  Row, 
  Col, 
  Card, 
  Button, 
  Typography, 
  Divider, 
  message,
  Collapse,
  Checkbox,
  Tabs,
  Input,
  Form,
  Empty
} from "antd";
import { 
  DndContext, 
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { 
  getSpecificMenu, 
  createMenuItem, 
  updateMenuItem, 
  deleteMenuItem,
  reorderMenuItems 
} from "../../../actions/ecommercemenuAction";
import { getAllCategory } from "../../../actions/categoryAction";
import { getAllTag } from "../../../actions/tagAction";
import { getAllpage } from "../../../actions/ecommercepageAction";
import MenuItem from "./MenuItem";

const { Title, Text } = Typography;
const { Panel } = Collapse;
const { TabPane } = Tabs;

const MenuManager = ({ 
  menuId, 
  getSpecificMenu, 
  createMenuItem, 
  updateMenuItem, 
  deleteMenuItem,
  reorderMenuItems,
  getAllCategory,
  getAllTag,
  getAllpage
}) => {
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedPages, setSelectedPages] = useState([]);
  const [customLink, setCustomLink] = useState({ title: "", url: "" });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchData();
  }, [menuId]);

  const fetchData = async () => {
    setLoading(true);
    const menuData = await getSpecificMenu(menuId);
    if (menuData) {
      // menuData.items is sorted by order in backend
      setMenuItems(flattenItems(menuData.items));
    }
    
    // Fetch options
    const catData = await getAllCategory();
    if (catData) setCategories(catData);
    
    const tagData = await getAllTag();
    if (tagData) setTags(tagData);
    
    const pageData = await getAllpage();
    if (pageData) setPages(pageData);
    
    setLoading(false);
  };

  // For DND we might want a flat list if we don't support true nested DND yet
  const flattenItems = (items, parentId = null, level = 0) => {
    let result = [];
    items.forEach(item => {
      result.push({ ...item, parentId, level });
      if (item.children && item.children.length > 0) {
        result = [...result, ...flattenItems(item.children, item.id, level + 1)];
      }
    });
    return result;
  };

  const handleAddItem = async (items, type) => {
    for (const item of items) {
      const newItem = {
        menu: menuId,
        title: item.title || item.name,
        url: item.url || (type === 'category' ? `/category/${item.slug}` : type === 'tag' ? `/tag/${item.slug}` : `/${item.slug}`),
        item_type: type,
        item_id: item.id,
        order: menuItems.length,
        is_mega_menu: false
      };
      await createMenuItem(newItem);
    }
    fetchData();
    // Clear selections
    if (type === 'category') setSelectedCategories([]);
    if (type === 'tag') setSelectedTags([]);
    if (type === 'page') setSelectedPages([]);
  };

  const handleAddCustomLink = async () => {
    if (!customLink.title || !customLink.url) return;
    const newItem = {
      menu: menuId,
      title: customLink.title,
      url: customLink.url,
      item_type: 'custom',
      order: menuItems.length
    };
    await createMenuItem(newItem);
    setCustomLink({ title: "", url: "" });
    fetchData();
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = menuItems.findIndex((item) => item.id === active.id);
      const newIndex = menuItems.findIndex((item) => item.id === over.id);

      const newOrderedItems = arrayMove(menuItems, oldIndex, newIndex);
      setMenuItems(newOrderedItems);

      const reorderData = newOrderedItems.map((item, index) => ({
        id: item.id,
        order: index
      }));

      const res = await reorderMenuItems(reorderData);
      if (res) {
        message.success("Menu updated");
      }
    }
  };

  const removeItem = async (id) => {
    await deleteMenuItem(id);
    fetchData();
  };

  const updateItem = async (id, values) => {
    await updateMenuItem(id, values);
    fetchData();
    message.success("Item updated");
  };

  const flattenNested = (items) => {
    let result = [];
    items.forEach(item => {
      // Create a copy without the children array to avoid confusion in buildTree
      const { children, ...rest } = item;
      result.push(rest);
      if (children && children.length > 0) {
        result = [...result, ...flattenNested(children)];
      }
    });
    return result;
  };

  const buildTree = (items, parentField = 'Category_parent', parentId = null, level = 0) => {
    let result = [];
    items.filter(item => {
      // Handle both null and undefined, and ensure type matching
      const itemParent = item[parentField];
      return (itemParent === parentId) || (itemParent?.id === parentId);
    }).forEach(item => {
      result.push({ ...item, level });
      result = [...result, ...buildTree(items, parentField, item.id, level + 1)];
    });
    return result;
  };

  const categoryTree = buildTree(flattenNested(categories));

  return (
    <Row gutter={24}>
      <Col span={6}>
        <Collapse defaultActiveKey={['1']}>
          <Panel header="Pages" key="1">
            <div style={{ maxHeight: 200, overflowY: 'auto', marginBottom: 10 }}>
              {pages.map(page => (
                <div key={page.id}>
                  <Checkbox 
                    checked={selectedPages.includes(page.id)}
                    onChange={e => {
                      if (e.target.checked) setSelectedPages([...selectedPages, page.id]);
                      else setSelectedPages(selectedPages.filter(id => id !== page.id));
                    }}
                  >
                    {page.name}
                  </Checkbox>
                </div>
              ))}
            </div>
            <Button 
              size="small" 
              onClick={() => handleAddItem(pages.filter(p => selectedPages.includes(p.id)), 'page')}
              disabled={selectedPages.length === 0}
            >
              Add to Menu
            </Button>
          </Panel>
          <Panel header="Categories" key="2">
            <div style={{ maxHeight: 300, overflowY: 'auto', marginBottom: 10 }}>
              {categoryTree.map(cat => (
                <div key={cat.id} style={{ marginLeft: (cat.level || 0) * 15 }}>
                  <Checkbox 
                    checked={selectedCategories.includes(cat.id)}
                    onChange={e => {
                      if (e.target.checked) setSelectedCategories([...selectedCategories, cat.id]);
                      else setSelectedCategories(selectedCategories.filter(id => id !== cat.id));
                    }}
                  >
                    {cat.name}
                  </Checkbox>
                </div>
              ))}
            </div>
            <Button 
              size="small" 
              onClick={() => handleAddItem(categoryTree.filter(c => selectedCategories.includes(c.id)), 'category')}
              disabled={selectedCategories.length === 0}
            >
              Add to Menu
            </Button>
          </Panel>
          <Panel header="Tags" key="3">
            <div style={{ maxHeight: 200, overflowY: 'auto', marginBottom: 10 }}>
              {tags.map(tag => (
                <div key={tag.id}>
                  <Checkbox 
                    checked={selectedTags.includes(tag.id)}
                    onChange={e => {
                      if (e.target.checked) setSelectedTags([...selectedTags, tag.id]);
                      else setSelectedTags(selectedTags.filter(id => id !== tag.id));
                    }}
                  >
                    {tag.name}
                  </Checkbox>
                </div>
              ))}
            </div>
            <Button 
              size="small" 
              onClick={() => handleAddItem(tags.filter(t => selectedTags.includes(t.id)), 'tag')}
              disabled={selectedTags.length === 0}
            >
              Add to Menu
            </Button>
          </Panel>
          <Panel header="Custom Links" key="4">
            <Form layout="vertical">
              <Form.Item label="URL" style={{ marginBottom: 8 }}>
                <Input size="small" value={customLink.url} onChange={e => setCustomLink({ ...customLink, url: e.target.value })} placeholder="http://"/>
              </Form.Item>
              <Form.Item label="Link Text" style={{ marginBottom: 8 }}>
                <Input size="small" value={customLink.title} onChange={e => setCustomLink({ ...customLink, title: e.target.value })} />
              </Form.Item>
              <Button size="small" onClick={handleAddCustomLink}>Add to Menu</Button>
            </Form>
          </Panel>
        </Collapse>
      </Col>
      
      <Col span={18}>
        <div style={{ backgroundColor: '#fff', border: '1px solid #d9d9d9', borderRadius: 4, padding: 16 }}>
          <Title level={5}>Menu Structure</Title>
          <Text type="secondary" style={{ fontSize: 13 }}>
            Arrange your menu items. Use the "Edit" button on items to set parents (sub-menus) or mark them as Mega Menus.
          </Text>
          <Divider style={{ margin: '16px 0' }} />
          
          <DndContext 
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext 
              items={menuItems.map(item => item.id)}
              strategy={verticalListSortingStrategy}
            >
              <div style={{ minHeight: 100 }}>
                {menuItems.map(item => (
                  <MenuItem 
                    key={item.id} 
                    id={item.id} 
                    item={item} 
                    allItems={menuItems}
                    removeItem={removeItem}
                    updateItem={updateItem}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
          
          {menuItems.length === 0 && <Empty description="Empty Menu" />}
        </div>
      </Col>
    </Row>
  );
};

export default connect(null, { 
  getSpecificMenu, 
  createMenuItem, 
  updateMenuItem, 
  deleteMenuItem,
  reorderMenuItems,
  getAllCategory,
  getAllTag,
  getAllpage
})(MenuManager);
